import type { PageServerMessageData } from "./pageInitialDataTypes";
import { TranslationType, type TranslationModel } from "~/common/types/TranslationModel";

/**
 * Normalizes a backend {@link PageServerMessageData} into the Redux toast message shape consumed by
 * {@link addMessages}. Shared by the page-data loader (redirect messages dispatched before a
 * client-side/external navigation) and the shell seed path (initial-render + stashed messages).
 */
export function normalizeServerMessage(message: PageServerMessageData) {
  const severity = message.severity ?? message.type ?? "info";

  return {
    severity,
    message: {
      type: TranslationType.RAW_TEXT,
      value: message.message,
    } as TranslationModel,
  };
}

/**
 * sessionStorage key carrying redirect server messages across a full-page navigation to a target
 * the SPA does not own (e.g. an external Zoovu content page). Replaces the former Spring FlashMap
 * transport; works identically under a future OCC backend.
 */
export const REDIRECT_SERVER_MESSAGES_KEY = "schaeffler.redirectServerMessages";

/**
 * Reads and clears stashed redirect server messages (if any). Called once on shell boot before
 * seeding from page metadata so messages produced by a previous SPA instance's redirect surface
 * on the target page.
 */
export function takeStashedRedirectMessages(): PageServerMessageData[] {
  try {
    const raw = sessionStorage.getItem(REDIRECT_SERVER_MESSAGES_KEY);
    if (!raw) {
      return [];
    }
    sessionStorage.removeItem(REDIRECT_SERVER_MESSAGES_KEY);
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as PageServerMessageData[]) : [];
  } catch {
    return [];
  }
}

/**
 * Stashes redirect server messages for the next SPA boot when the redirect target is not a
 * TanStack-owned route (external / absolute URL → full page reload). For SPA-owned relative paths
 * ("/cart") Redux already persists across the client-side navigation, so no relay is needed.
 */
export function stashRedirectMessagesIfNeeded(location: string | undefined, messages: PageServerMessageData[]): void {
  if (!messages.length || !isExternalRedirectTarget(location)) {
    return;
  }
  try {
    sessionStorage.setItem(REDIRECT_SERVER_MESSAGES_KEY, JSON.stringify(messages));
  } catch {
    // sessionStorage may be unavailable (private mode); the toast still surfaces for any
    // client-side portion of the navigation.
  }
}

function isExternalRedirectTarget(location: string | undefined): boolean {
  if (!location) {
    return false;
  }
  // Relative paths ("/cart", "/p/x") are SPA-owned → client-side nav, Redux persists.
  // Absolute ("https://...") or protocol-relative ("//host/...") URLs trigger a full reload.
  return !location.startsWith("/") || location.startsWith("//");
}
